import {
  MOBILE_VIEW_THRESHOLD_PX,
  ROUTE_LANDING,
  ROUTE_VERIFY_EMAIL,
} from '@1bill-app/constants';
import { SOCIAL_LOGIN_OPTS, SOCIAL_LOGIN_OPTS2 } from 'src/app/app.menu';
import { IonInput, ModalController, NavController, Platform } from '@ionic/angular';
import { AuthQuery } from './../../../services/auth/state/auth.query';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib/types/Auth';
import { AuthService } from './../../../services/auth/state/auth.service';
import {
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { SubSink } from 'subsink';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomOAuth } from '@1bill-app/services/auth/state/CustomOAuth';
import { NGXLogger } from 'ngx-logger';
import { map } from 'rxjs/operators';
import { environment } from '@1bill-app/env';
import { ROUTE_HOMEPAGE } from '@1bill-app/constants';
import { signInForm } from './form';
import { getValidationMsgs, ValidationMsgIF } from '@1bill-app/validators/messages';
import { FormGroup } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { RecoverConfirmModalComponent } from '../recover-password/recover-confirm-modal/recover-confirm-modal.component';
import { Keyboard } from '@capacitor/keyboard';

const FederatedProvider = CognitoHostedUIIdentityProvider;

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private authService: AuthService,
    private authQuery: AuthQuery,
    private navCtrl: NavController,
    private route: ActivatedRoute,
    private router: Router,
    private _zone: NgZone,
    private customOAuth: CustomOAuth,
    private logger: NGXLogger,
    private zone: NgZone,
    private modalCtrl: ModalController,
    private cdRef: ChangeDetectorRef,
    private platform: Platform,
  ) {
    this.userForm = signInForm();
    this.validationMsgs = getValidationMsgs();
  }

  @ViewChild('passwordInput', { static: true }) passwordInput: IonInput;
  @ViewChild('emailInput', { static: true }) emailInput: IonInput;
  public isNative = Capacitor.isNativePlatform();
  /* Flag property */
  public isLoading$ = this.authQuery.selectLoading();
  public isCheckingEmail: boolean;
  public isEmailRegistered: boolean;
  public enableNextBtn: boolean;
  public enableSigninBtn: boolean;
  public showValidationError: boolean;
  public serverValidationError: string = null;
  //
  landingPage = ROUTE_LANDING;
  socialSignInOpts = SOCIAL_LOGIN_OPTS;
  social = SOCIAL_LOGIN_OPTS2;

  subs = new SubSink();
  public loading$ = this.authQuery.isLoading$;
  public socialLoading$ = this.authQuery.isSocialLoading$;
  public authProvider$ = this.authQuery.authProvider$;
  public isGoogleProvider$ = this.isProviderLoading(FederatedProvider.Google);
  public isFacebookProvider$ = this.isProviderLoading(FederatedProvider.Facebook);
  public isAppleProvider$ = this.isProviderLoading(FederatedProvider.Apple);
  /* form property */
  userForm: FormGroup;
  validationMsgs: ValidationMsgIF;
  appTerms = environment.appTerms;
  appPrivacyPolicy = environment.appPrivacyPolicy;
  signInLoading = false;
  public passwordType: string;

  get isDesktopView() {
    return this.platform.width() >= MOBILE_VIEW_THRESHOLD_PX;
  }

  //
  get emailCtrl() {
    return this.userForm.get('email');
  }
  get passwordCtrl() {
    return this.userForm.get('password');
  }

  get emailClass() {
    return (this.showValidationError && this.emailCtrl.invalid && this.emailCtrl.touched) ||
      this.serverValidationError
      ? 'error'
      : '';
  }
  get pwdClass() {
    return (this.showValidationError &&
      this.passwordCtrl.invalid &&
      this.passwordCtrl.touched &&
      this.passwordCtrl.hasError('required')) ||
      this.serverValidationError
      ? 'error'
      : '';
  }

  async ngOnInit() {
    this.authService.clearAuthState();
    this.route.params.subscribe((params) => {
      if (params['code']) {
        // manually handle auth response
        // currently only for native apps
        try {
          this.customOAuth._handleAuthResponse(this.router.url).then(() => {
            this._zone.run(async () => {
              this.navCtrl.navigateRoot([ROUTE_HOMEPAGE]);
            });
          });
        } catch (e) {
          this.logger.error('login.page ngOnInit', e);
        }
      }
    });

    const nativeEmailInput = await this.emailInput.getInputElement();
    const nativePasswordInput = await this.passwordInput.getInputElement();

    // This is used to update form after user autofills the form via iOS keychain
    // Ref: https://github.com/ionic-team/ionic-framework/issues/23335#issuecomment-845270643
    nativeEmailInput.addEventListener('change', (ev: Event) => {
      requestAnimationFrame(() => {
        this.userForm.patchValue({ email: (ev.target as HTMLInputElement).value });
        // console.log('email update', (ev.target as HTMLInputElement).value)
      });
    });

    nativePasswordInput.addEventListener('change', (ev: Event) => {
      requestAnimationFrame(() => {
        this.userForm.patchValue({ password: (ev.target as HTMLInputElement).value });
        // console.log('pwd update', (ev.target as HTMLInputElement).value)
      });
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngAfterViewInit() {
    this.subs.sink = this.authQuery.isLoggedIn$.subscribe({
      next: (isLoggedIn) => {
        this.authService.clearProviderState();
        const code = this.route.snapshot.queryParamMap.get('code');
        if (code && isLoggedIn) {
          this.navCtrl.navigateRoot([ROUTE_HOMEPAGE]);
        }
      },
    });
  }

  onAppleSignin() {
    this._zone.run(async () => {
      this.authService.federatedSignIn(FederatedProvider.Apple).subscribe({
        next: () => {},
        error: this.handleError,
      });
    });
  }

  onFacebookSignin() {
    this._zone.run(async () => {
      this.authService.federatedSignIn(FederatedProvider.Facebook).subscribe({
        next: () => {},
        error: this.handleError,
      });
    });
  }

  onGoogleSignin() {
    this._zone.run(async () => {
      this.authService.federatedSignIn(FederatedProvider.Google).subscribe({
        next: () => {},
        error: this.handleError,
      });
    });
  }

  handleError(err) {
    this.authService.clearProviderState();
    this.logger.error(err);
  }

  isProviderLoading(provider: CognitoHostedUIIdentityProvider) {
    return this.authProvider$.pipe(map((p) => p === provider));
  }

  ionViewDidEnter() {
    const { email } = this.authQuery.getCredentials();
    this.userForm.patchValue({ email });
    if (email) this.enableNextBtn = true;
  }

  // async onClickNext() {
  //   this.showValidationError = true;
  //   if (!this.userForm.get('email').valid) return;

  //   const { email } = this.userForm.value;
  //   this.authService.setLoading(true);
  //   this.isCheckingEmail = true;

  //   const isNotExists = await this.authService.checkEmailNotExists(email);
  //   this.logger.debug('email already registered: ', !isNotExists);
  //   if (isNotExists) {
  //     this.navCtrl.navigateForward(['/auth/signup-with-email']);
  //     this.resetFormValues();
  //   } else {
  //     this.isEmailRegistered = true;
  //     this.userForm.get('email').disable();
  //     this.authService.setLoading(false);
  //     this.isCheckingEmail = false;
  //     setTimeout(() => {
  //       this.passwordInput.setFocus();
  //     }, 400);
  //   }
  // }

  async onClickSignin() {
    this.showValidationError = true;
    this.userForm.markAllAsTouched();
    const { email, password } = this.userForm.getRawValue();
    if (!email || !password) {
      return;
    }

    if (this.userForm.invalid) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      Keyboard.hide();
    }
    this.signInLoading = true;
    try {
      const result = await this.authService.login({ email, password }).toPromise();
      this.logger.debug('login response', result);
      if (result) {
        this.navCtrl.navigateRoot([ROUTE_HOMEPAGE]);
      }
      this.resetFormValues();
    } catch (error) {
      this.logger.warn(error);
      if (error.code) {
        switch (error.code) {
          case 'UserNotConfirmedException':
            {
              this.navCtrl.navigateForward([ROUTE_VERIFY_EMAIL], {
                state: { email, password },
              });
            }
            break;
          case 'NotAuthorizedException':
            {
              if (error.message?.includes('User is disabled')) {
                this.serverValidationError =
                  'Your account has been terminated due to suspicious or fraudulent activity. If you believe this is a mistake, please get in touch at https://www.1bill.com/contact';
              } else {
                this.serverValidationError =
                  error.message || 'Incorrect username or password.';
              }
            }
            break;
          case 'UserNotFoundException':
            {
              this.serverValidationError = error.message || 'Incorrect username or password.';
            }
            break;
          case 'PasswordResetRequiredException':
            {
              await this.presentRecoverPasswordConfirmModal({ email });
            }
            break;
        }
      }
    }
    this.signInLoading = false;
  }

  onEmailInputChange(e) {
    this.serverValidationError = null;
    this.enableNextBtn = e.detail.value.length > 0 ? true : false;
    this.cdRef.detectChanges();
  }

  onPasswordInputChange(e) {
    this.serverValidationError = null;
    this.enableSigninBtn = e.detail.value.length > 0 ? true : false;
  }

  resetFormValues() {
    this.userForm.get('email').enable();
    this.userForm.reset();
    this.isEmailRegistered = null;
    this.isCheckingEmail = null;
  }

  async presentRecoverPasswordConfirmModal(props: { email: string }) {
    const modal = await this.modalCtrl.create({
      component: RecoverConfirmModalComponent,
      cssClass: 'modal--auth-recover-confirm',
      componentProps: { ...props, type: 'reset_password' },
    });
    await modal.present();
    return modal;
  }

  togglePasswordType() {
    this.passwordType = this.passwordType || 'password';
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    this.cdRef.detectChanges();
  }
}
